/* Turn a drawn picture into a file that stands on its own.
 *
 * **The SVG in the page is not a file.** `diagram.js` and `flowchart.js` write
 * markup whose every colour and font comes from a class in the site
 * stylesheet, resolved through the theme's tokens and `color-mix`. Saved as it
 * stands it opens as black text on nothing. So an export FREEZES the styles:
 * each class attribute becomes a style attribute carrying the values the
 * browser actually computed, a backing rectangle is put behind the drawing in
 * the page's own background colour, and a margin is added so the drawing does
 * not touch the edge of the file. The picture a reader downloads is the
 * picture they were looking at, in whichever theme they were looking at it.
 *
 * **Pure string in, pure string out.** The one thing that needs a browser,
 * asking what a class computes to, arrives as a callback, so everything here
 * runs under `node` in `run-export.mjs` and the DOM layer in `tester.js` stays
 * thin. That is the same split `highlight.js` made, and for the same reason:
 * the edge cases are on this side.
 */

/** The CSS properties the stylesheet sets on the drawings, and nothing else. */
export const PROPERTIES = [
  "fill",
  "stroke",
  "stroke-width",
  "stroke-dasharray",
  "stroke-linecap",
  "stroke-linejoin",
  "font-family",
  "font-size",
  "font-weight",
];

/** Breathing room around the drawing, in its own units. */
export const MARGIN = 16;

/** PNGs are drawn at this many device pixels per SVG unit, where the browser allows. */
export const PNG_SCALE = 3;

// The smallest canvas any browser we care about refuses: iOS Safari stops at
// 16,777,216 pixels of area and every engine stops somewhere around 16,384 on
// a side. A long trace can be thousands of units wide, so the scale gives way
// rather than the export failing with a blank image and no error.
const MAX_AREA = 16_777_216;
const MAX_SIDE = 16_384;

/**
 * The drawing as a self-contained SVG document.
 *
 * @param {string} svg the markup as drawn, beginning `<svg`
 * @param {(tag: string, classes: string) => string} styleOf what an element
 *   with this tag and this class attribute computes to, as a `style` attribute
 *   value with no double quotes in it, or "" for nothing worth keeping
 * @param {string} background the colour behind the drawing
 * @param {number} margin units of space around the drawing
 * @returns {{ svg: string, width: number, height: number }}
 */
export function standalone(svg, styleOf, background, margin = MARGIN) {
  const head = /^<svg\b([^>]*)>/.exec(svg);

  if (!head || !svg.endsWith("</svg>")) {
    throw new Error("not a drawing");
  }

  const drawnWidth = Number(attribute(head[1], "width"));
  const drawnHeight = Number(attribute(head[1], "height"));

  if (!(drawnWidth > 0 && drawnHeight > 0)) {
    throw new Error("the drawing has no size");
  }

  const width = drawnWidth + 2 * margin;
  const height = drawnHeight + 2 * margin;

  let body = svg.slice(head[0].length, -"</svg>".length);

  // Every class becomes what it computed to, wherever in the tag it sits: the
  // drawings mostly put it first, and the flowchart's arrowhead puts it after
  // the path data.
  body = body.replace(/<(\w+)\b([^>]*?) class="([^"]*)"/g, (all, tag, before, classes) => {
    const style = styleOf(tag, classes);
    return style.length > 0 ? `<${tag}${before} style="${style}"` : `<${tag}${before}`;
  });

  // The hooks the page's own script listens on mean nothing in a file, and a
  // tabindex on a shape is noise in every viewer.
  body = body.replace(/ (?:data-[\w-]+|tabindex)="[^"]*"/g, "");

  // The root keeps its accessible name and loses its class; width, height and
  // viewBox all grow by the margin, and the viewBox's origin moves so the
  // drawing's own coordinates are untouched.
  const kept = head[1]
    .replace(/ class="[^"]*"/, "")
    .replace(/ width="[^"]*"/, "")
    .replace(/ height="[^"]*"/, "")
    .replace(/ viewBox="[^"]*"/, "");

  const root =
    `<svg${kept} width="${width}" height="${height}" ` +
    `viewBox="${-margin} ${-margin} ${width} ${height}">`;

  const backing =
    `<rect x="${-margin}" y="${-margin}" width="${width}" height="${height}" fill="${background}"/>`;

  return { svg: `${root}${backing}${body}</svg>`, width, height };
}

/**
 * The same document at another size. The viewBox stays, so a browser asked to
 * draw this into a canvas rasterises the vector at the new size rather than
 * rasterising at the old one and scaling the pixels, which is what Safari does
 * when only the canvas is bigger.
 */
export function resized(svg, width, height) {
  return svg
    .replace(/^(<svg\b[^>]*?) width="[^"]*"/, `$1 width="${Math.round(width)}"`)
    .replace(/^(<svg\b[^>]*?) height="[^"]*"/, `$1 height="${Math.round(height)}"`);
}

/**
 * How many pixels per unit a PNG of this size can be drawn at: the preferred
 * scale, stepped down by halves until the canvas fits, never below 1.
 */
export function pngScale(width, height) {
  let scale = PNG_SCALE;

  while (scale > 1 && (width * height * scale * scale > MAX_AREA || Math.max(width, height) * scale > MAX_SIDE)) {
    scale -= 0.5;
  }

  return scale;
}

function attribute(attributes, name) {
  const found = new RegExp(` ${name}="([^"]*)"`).exec(attributes);
  return found ? found[1] : null;
}
